import { useMutation } from '@apollo/client';

import { InviteTeamMemberRoleInput, TeamMemberRole } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import {
  EditableRole,
  MemberInformationRoleFormSchema,
} from 'features/Team/components/MemberInformationRoleForm/schema';

import { refetchTeamMembersQuery } from '../../../../hooks/useTeamMembers';
import { refetchTeamMembersInvitationQuotaQuery } from '../../../../hooks/useTeamMembersInvitationQuota';
import { MemberInformationFormSchema } from '../../components/MemberInviteStepper/steps/MemberInformation/MemberInformationForm/schema';
import { InviteTeamMemberDocument } from './inviteTeamMember.gql';

const mapMemberInformationFormRoleToInviteTeamMemberRoleInput = (
  role: EditableRole,
): InviteTeamMemberRoleInput => {
  if (role === TeamMemberRole.Admin) {
    return InviteTeamMemberRoleInput.Admin;
  }
  if (role === TeamMemberRole.Accountant) {
    return InviteTeamMemberRoleInput.Accountant;
  }
  if (role === TeamMemberRole.Employee) {
    return InviteTeamMemberRoleInput.Employee;
  }
  throw new Error('InviteTeamMemberMutation used with an unhandled role');
};

export const useInviteTeamMember = () => {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error("'companyProfileId' is undefined in 'useInviteTeamMember'");
  }

  const [mutation, hooks] = useMutation(InviteTeamMemberDocument);

  const submitInviteMemberRequest = (
    input: MemberInformationRoleFormSchema & MemberInformationFormSchema,
  ) => {
    return mutation({
      refetchQueries: [
        refetchTeamMembersQuery(companyProfileId),
        refetchTeamMembersInvitationQuotaQuery(companyProfileId),
      ],
      variables: {
        input: {
          companyProfileId: companyProfileId,
          ...input,
          role: mapMemberInformationFormRoleToInviteTeamMemberRoleInput(
            input.role,
          ),
        },
      },
    });
  };

  return [submitInviteMemberRequest, hooks] as const;
};
